import { Component, Input } from '@angular/core';
import { UserService } from '../../services/user.service';
import { RequestsServices } from '../../services/requests.service';
import { ToasterService } from '../../services/toaster.service';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { EstablishmentsService } from '../../services/establishments.service';
import { CashbackService } from '../../services/cashback.service';
import { StorageService } from '../../services/storage.service';
import * as _ from 'lodash';
import {ICategory} from '../../interfaces/ICategory';
import {ISubcategory} from '../../interfaces/ISubcategory';

@Component({
  selector: 'search-container',
  templateUrl: './search-container.component.html'
})
export class SearchContainerComponent {

  @Input() ownPartnershipsOnly = false;

  loggedUser;
  activeAccount;
  addresses = [];
  categories: Array<ICategory> = [];
  currentLocation: string;
  defaultLocation = '-23.550717,-46.608708';
  establishments = [];
  establishmentsOnline = [];
  loadingEstablishments = false;
  loadingOnlineEstablishments = true;
  cashbackPartners = [];
  loadingCashback = false;
  searchType = { online: true, near: true, nearSearchLabel: '', fromLocationSearch: false, seeMore: false };
  pagination = { online: { count: 0, limit: 60, skip: 0 }, near: { count: 0, limit: 30, skip: 0 } };
  searchParams = { name: '', location: this.defaultLocation, categories: [], subcategories: [], invite: '' };
  scrolling = false;
  completeGetLocation = false;

  constructor(
    private userService: UserService,
    private request: RequestsServices,
    private toaster: ToasterService,
    private activatedRoute: ActivatedRoute,
    private establishmentsService: EstablishmentsService,
    private cashbackService: CashbackService,
    private router: Router,
    private storage: StorageService
  ) {
    this.getCurrentUser();
    this.getAddresses();
    this.getCategories().then(() => {
      this.getSearchFromUrl();
    });
  }

  get loading() {
    return !this.scrolling && (this.loadingOnlineEstablishments || this.loadingEstablishments || this.loadingCashback);
  }

  getAddresses = () => this.addresses = this.userService.getAccountAddresses();

  getCurrentUser = () => {
    this.loggedUser = this.userService.getLoggedUser();
    this.activeAccount = this.userService.getLoggedAccount();
  }

  getCategories() {
    return this.request
      .get({ path: 'categories' })
      .then(result => this.categories = result.map(category => this.mapCategory(category)))
      .catch(err => {
        this.toaster.info(
          'Categorias',
          'Não foi possível obter as categorias! Recarregue a página e tente novamente.',
          false,
          5000
        );
      });
  }

  mapCategory(categoryResponse): ICategory {
    const category = {
      id: categoryResponse.id,
      name: categoryResponse.name,
      slug: categoryResponse.slug,
      subcategories: categoryResponse.subcategories.map(subcategory => this.mapSubcategory(subcategory)),
      checked: false,
      showSubcategories: false
    };

    return category;
  }

  mapSubcategory(subcategory): ISubcategory {
    return {
      checked: false,
      ...subcategory
    };
  }

  search() {
    const { categories, subcategories, ...searchParams} = this.searchParams;
    const params = { ...searchParams, companyId: this.activeAccount.id } as any;

    if (categories.length) { params.categories = JSON.stringify(this.searchParams.categories); }

    if (subcategories.length) { params.subcategories = JSON.stringify(this.searchParams.subcategories); }

    this.resetSearch();
    this.setSearchUrlParams(params);
    this.onlineSearch(params);
    this.nearSearch(params);
    this.cashbackSearch(params);
  }

  cashbackSearch(searchParams) {
    // A API de cashback não filtra por categoria: com categoria selecionada a seção não aparece
    if (this.ownPartnershipsOnly || searchParams.categories || searchParams.subcategories) {
      this.cashbackPartners = [];
      this.loadingCashback = false;
      return;
    }

    const params = { skip: 0, limit: 4 } as any;
    if (searchParams.name) { params.search = searchParams.name; }

    this.loadingCashback = true;

    this.cashbackService
      .listPartners(params)
      .subscribe(result => {
        this.loadingCashback = false;
        this.cashbackPartners = (result && result.data) || [];
      }, err => {
        this.loadingCashback = false;
        this.cashbackPartners = [];
      });
  }

  onlineSearch(searchParams) {
    const params = {
      ...searchParams,
      ...this.pagination.online,
      limit: 30
    };
    const { skip, count } = this.pagination.online;

    this.loadingOnlineEstablishments = true;

    if (this.ownPartnershipsOnly) {
      params['own_partnerships'] = 'only';
    } else {
      params['own_partnerships'] = 'show';
    }

    if (skip <= count) {
      this.establishmentsService
        .searchOnlineByName(params)
        .subscribe(result => {
          result.data.sort(() => .5 - Math.random());
          this.loadingOnlineEstablishments = false;
          this.establishmentsOnline = this.scrolling ? this.establishmentsOnline.concat(result.data) : result.data;
          this.pagination.online.skip += result.limit;
          this.pagination.online.count += result.data.length;
          this.scrolling = false;
        }, err => {
          this.loadingOnlineEstablishments = false;
          this.scrolling = false;
        });
    } else {
      this.loadingOnlineEstablishments = false;
      this.scrolling = false;
    }
  }

  resetSearch() {
    this.establishments = [];
    this.establishmentsOnline = [];
    this.cashbackPartners = [];
    this.pagination.online.skip = 0;
    this.pagination.online.count = 0;
    this.pagination.near.skip = 0;
    this.pagination.near.count = 0;
  }

  nearSearch(searchParams) {
    const params = { ...searchParams, ...this.pagination.near };
    const { skip, count } = this.pagination.near;

    if (this.ownPartnershipsOnly) {
      params['own_partnerships'] = 'only';
    } else {
      params['own_partnerships'] = 'show';
    }

    this.loadingEstablishments = true;

    if (skip <= count) {
      // Com termo de busca sempre filtra por nome, mesmo sem localização (usa a localização padrão para ordenar)
      if (params.name) {
        this.establishmentsService.searchByName(params).subscribe(this.onSuccessNearSearch, this.onFailedNearSearch);
      } else {
        this.establishmentsService.exploreLocation(params).subscribe(this.onSuccessNearSearch, this.onFailedNearSearch);
      }
    } else {
      this.loadingEstablishments = false;
      this.scrolling = false;
    }
  }

  onSuccessNearSearch = result => {
    this.loadingEstablishments = false;
    this.establishments = this.scrolling ? this.establishments.concat(result.data) : result.data;
    this.pagination.near.skip += result.limit;
    this.pagination.near.count += result.data.length;
    this.scrolling = false;
  };

  onFailedNearSearch = err => {
    this.loadingEstablishments = false;
    this.scrolling = false;
  };

  getSearchFromUrl() {
    this.activatedRoute.queryParams.subscribe((params: Params) => {
      const { search, categories, location, invite } = params;

      if (search) {
        this.searchParams.name = search;
      }

      if (location) {
        this.searchParams.location = location;
        this.storage.set('selectedLocation', location);
      }

      if (invite) {
        this.searchParams.invite = invite;
        this.storage.set('invite', invite);
      }

      if (categories) {
        this.searchParams.categories = this.getCategoriesAsQuerySearch(categories);
      }

      this.getMyLocation(location);

    }).unsubscribe();
  }

  setSearchUrlParams(params) {
    const { name, categories, location, invite } = params;
    const queryParams = {};

    if (name) {
      queryParams['search'] = name;
    }

    if (location) {
      queryParams['location'] = location;
      this.storage.set('selectedLocation', location);
    }

    if (invite) {
      queryParams['invite'] = invite;
    }

    if (categories) {
      queryParams['categories'] = this.getCategoriesAsUrlParam(JSON.parse(categories));
    }

    const navigationExtras: NavigationExtras = { queryParams };

    if (!_.isEqual(navigationExtras.queryParams, this.activatedRoute.snapshot.queryParamMap['params'])) {
      const redirectUrl = this.ownPartnershipsOnly ? '/parcerias-exclusivas' : '/listagem';
      this.router.navigate([redirectUrl], navigationExtras);
    }
  }

  getCategoriesAsUrlParam(selectedCategories) {
    const categoriesUrlParam = [];
    for (const selectedCategory of selectedCategories) {
      const subcategoriesSlug = [];
      for (const subcategory of selectedCategory.subcategories) {
        subcategoriesSlug.push(subcategory.slug);
      }
      if (subcategoriesSlug.length > 0) {
        categoriesUrlParam.push(`${selectedCategory.slug}.${subcategoriesSlug.join('.')}`) ;
      } else {
        categoriesUrlParam.push(`${selectedCategory.slug}`);
      }
    }

    return categoriesUrlParam.join(',');
  }

  getCategoriesAsQuerySearch(stringUrlSlugCategories): any[] {
    const categories = [];
    const listUrlSlugCategories = stringUrlSlugCategories.split(',');
    for (const urlSlugCategory of listUrlSlugCategories) {
      const urlSlugCategoryAndSubcategoriesSlug = urlSlugCategory.split('.');
      const category = this.categories.find(c => c.slug === urlSlugCategoryAndSubcategoriesSlug[0]);
      if (!category) {
        continue;
      }

      const subcategories = [];
      for (let i = 1; i < urlSlugCategoryAndSubcategoriesSlug.length; i++) {
        const subcategorySlug = urlSlugCategoryAndSubcategoriesSlug[i];
        const subcategory = category.subcategories.find(s => s.slug === subcategorySlug);
        if (!subcategory) {
          continue;
        }
        subcategories.push({id: subcategory.id, slug: subcategory.slug});
      }

      categories.push({
        id: category.id,
        slug: category.slug,
        subcategories: subcategories.map(s => ({id: s.id, slug: s.slug}))
      });
    }

    return categories;
  }

  getMyLocation(location) {
    this.completeGetLocation = false;

    if (this.storage.get('currentLocation')) {
      this.successGetLocation(location)(this.storage.get('currentLocation'));
    }

    navigator.geolocation.getCurrentPosition(
      this.successGetLocation(location),
      this.failedGetLocation('Para identificar estabelecimentos próximos, precisamos que nos permita sua localização!')
    );

    setTimeout(() => {
      if (!this.completeGetLocation) {
        this.failedGetLocation(
          'Houve uma demora para obter sua localização atual e carregamos uma localização padrão.',
          10000
        )();
      }
    }, 5000);
  }

  successGetLocation = preSetLocation => pos => {
    this.currentLocation = (typeof pos === 'string') ? pos : `${pos.coords.latitude},${pos.coords.longitude}`;
    this.storage.set('currentLocation', this.currentLocation);

    if (!this.completeGetLocation) {
      const location = preSetLocation ? preSetLocation : this.currentLocation;
      this.searchParams = { ...this.searchParams, location };
      this.completeGetLocation = true;
      this.search();
    }
  };

  failedGetLocation = (message, toastLife = 5000) => () => {
    if (!this.completeGetLocation) {
      this.completeGetLocation = true;
      this.toaster.info('Localização', message, false, toastLife);

      if (this.addresses.length) {
        const { lat, lng } = this.addresses[0].location;
        this.searchParams.location = `${lat},${lng}`;
      } else {
        this.searchType.near = false;
      }

      this.search();
      this.storage.remove('currentLocation');
    }
  };

  scrollSearch() {
    const { categories, ...searchParams } = this.searchParams;
    const params = { ...searchParams, companyId: this.activeAccount.id };
    if (categories.length) { params['categories'] = JSON.stringify(this.searchParams.categories); }

    this.scrolling = true;
    this.setSearchUrlParams(params);

    if (this.searchType.seeMore) {
      this.onlineSearch(params);
    } else {
      this.nearSearch(params);
    }
  }

}
